import { useState } from "react";
import { Film, Lock } from "lucide-react";
import { login, setPassword, formatApiError } from "../lib/api";
import { useAuth } from "../auth/AuthContext";

export default function Login() {
  const { signIn } = useAuth();
  const [mode, setMode] = useState("login"); // login | setPassword
  const [email, setEmail] = useState("");
  const [password, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submitLogin = async () => {
    setError("");
    if (!email.trim()) return setError("Please enter your email.");
    setBusy(true);
    try {
      const res = await login(email.trim(), password);
      if (res.must_set_password) {
        setMode("setPassword");
        setPwd("");
      } else {
        signIn(res.token, res.user);
      }
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Couldn't sign in.");
    } finally {
      setBusy(false);
    }
  };

  const submitSetPassword = async () => {
    setError("");
    if (password.length < 6) return setError("Please choose a password with at least 6 characters.");
    if (password !== confirm) return setError("The two passwords don't match.");
    setBusy(true);
    try {
      const res = await setPassword(email.trim(), password);
      signIn(res.token, res.user);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Couldn't set your password.");
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e, fn) => e.key === "Enter" && fn();
  const isSet = mode === "setPassword";
  const [showForgot, setShowForgot] = useState(false);

  return (
    <div className="min-h-screen flex items-center justify-center px-5">
      <div className="w-full max-w-md animate-fade-up">
        <div className="mb-8 flex flex-col items-center text-center">
          <div className="grid h-16 w-16 place-items-center rounded-2xl bg-[#0A84FF] mb-5">
            <Film className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-white">Kane Family Videos</h1>
          <p className="mt-2 text-lg text-white/50">
            {isSet ? "Welcome! Create a password to finish setting up." : "Sign in to continue."}
          </p>
        </div>

        <div className="rounded-3xl bg-[#1c1c1e] p-6 ring-1 ring-white/10">
          <label className="mb-1.5 block text-[15px] font-medium text-white/50">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => onKey(e, submitLogin)}
            disabled={isSet}
            placeholder="you@example.com"
            className="mb-4 h-14 w-full rounded-2xl bg-white/[0.06] px-5 text-xl text-white placeholder-white/35 outline-none ring-1 ring-white/10 focus:ring-2 focus:ring-[#0A84FF] transition-all disabled:opacity-60"
            data-testid="login-email-input"
          />

          {!isSet && (
            <>
              <label className="mb-1.5 block text-[15px] font-medium text-white/50">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPwd(e.target.value)}
                onKeyDown={(e) => onKey(e, submitLogin)}
                placeholder="Your password"
                className="h-14 w-full rounded-2xl bg-white/[0.06] px-5 text-xl text-white placeholder-white/35 outline-none ring-1 ring-white/10 focus:ring-2 focus:ring-[#0A84FF] transition-all"
                data-testid="login-password-input"
              />
            </>
          )}

          {isSet && (
            <>
              <label className="mb-1.5 block text-[15px] font-medium text-white/50">New password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPwd(e.target.value)}
                onKeyDown={(e) => onKey(e, submitSetPassword)}
                placeholder="Choose a password"
                className="mb-4 h-14 w-full rounded-2xl bg-white/[0.06] px-5 text-xl text-white placeholder-white/35 outline-none ring-1 ring-white/10 focus:ring-2 focus:ring-[#0A84FF] transition-all"
                data-testid="set-password-input"
              />
              <label className="mb-1.5 block text-[15px] font-medium text-white/50">Confirm password</label>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                onKeyDown={(e) => onKey(e, submitSetPassword)}
                placeholder="Type it again"
                className="h-14 w-full rounded-2xl bg-white/[0.06] px-5 text-xl text-white placeholder-white/35 outline-none ring-1 ring-white/10 focus:ring-2 focus:ring-[#0A84FF] transition-all"
                data-testid="confirm-password-input"
              />
            </>
          )}

          {error && (
            <p className="mt-4 text-base text-[#FF453A]" data-testid="login-error">
              {error}
            </p>
          )}

          <button
            onClick={isSet ? submitSetPassword : submitLogin}
            disabled={busy}
            className="mt-6 inline-flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-[#0A84FF] text-lg font-semibold text-white hover:bg-[#409CFF] transition-colors disabled:opacity-60"
            data-testid="login-submit-button"
          >
            <Lock className="h-5 w-5" />
            {busy ? "Please wait…" : isSet ? "Set password & sign in" : "Sign in"}
          </button>

          {!isSet && (
            <div className="mt-5 text-center">
              <button
                onClick={() => setShowForgot((s) => !s)}
                className="text-base font-medium text-[#0A84FF] hover:opacity-80 transition-opacity"
                data-testid="forgot-password-link"
              >
                Forgot password?
              </button>
              {showForgot && (
                <p className="mt-3 text-base leading-relaxed text-white/55" data-testid="forgot-password-help">
                  Ask the family admin (<span className="text-white/80">j@kane.email</span>) to reset
                  it for you. After they do, just sign in with your email and you'll be asked to
                  choose a new password.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
